// PlatformHealth — the read-only "is everything working?" dashboard.
// CLAUDE.md §7: a screen that answers the question so nobody has to call us.
// Reads /api/platform-health (tenant-scoped) and refreshes itself every 30s.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Info, MessageSquare,
  ScanLine, Users, Database, CreditCard,
} from 'lucide-react';
import api from '../../services/api';

const LOOK = {
  ok:   { box: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', Icon: CheckCircle2,  iconColor: 'text-emerald-600' },
  warn: { box: 'bg-amber-50 border-amber-200',     text: 'text-amber-900',   Icon: AlertTriangle, iconColor: 'text-amber-600'  },
  down: { box: 'bg-red-50 border-red-200',         text: 'text-red-800',     Icon: XCircle,       iconColor: 'text-red-600'    },
  info: { box: 'bg-slate-50 border-slate-200',     text: 'text-slate-700',   Icon: Info,          iconColor: 'text-slate-400'  },
};

const COMPONENT_ICON = {
  messaging: MessageSquare,
  screening: ScanLine,
  leads:     Users,
  database:  Database,
  payments:  CreditCard,
};

function fmt(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

// @story HIR-HR10
export default function PlatformHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);

  const load = useCallback(() => {
    setRefreshing(true);
    return api.get('/platform-health')
      .then((r) => { setData(r.data.data); setErr(''); setLastLoaded(new Date()); })
      .catch(() => setErr('Could not read the health status right now. It will retry; if this persists, tell your developer.'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refreshes itself every 30 seconds
    return () => clearInterval(t);
  }, [load]);

  const overall = data?.overall;
  const healthy = overall === 'healthy';

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto font-outfit">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-meraki bg-meraki-indigo/10 flex items-center justify-center">
            <Activity className="text-meraki-indigo" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-meraki-graphite">Platform health</h1>
            <p className="text-sm text-slate-500">Live view of the recruitment platform — refreshes on its own.</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="flex items-center gap-2 text-sm font-semibold text-meraki-indigo hover:bg-meraki-indigo/5 px-3 py-2 rounded-meraki transition disabled:opacity-50"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading && !data && (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-slate-500">
          Checking the platform…
        </div>
      )}

      {err && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 mb-6">
          {err}
        </div>
      )}

      {data && (
        <>
          {/* Overall banner */}
          <div className={`rounded-2xl border px-6 py-5 mb-6 flex items-center gap-4 ${healthy ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            {healthy
              ? <CheckCircle2 className="text-emerald-600 shrink-0" size={36} />
              : <AlertTriangle className="text-amber-600 shrink-0" size={36} />}
            <div>
              <div className={`text-xl font-bold ${healthy ? 'text-emerald-800' : 'text-amber-900'}`}>
                {healthy ? 'All systems healthy' : 'Needs a look'}
              </div>
              <div className={`text-sm ${healthy ? 'text-emerald-700' : 'text-amber-800'}`}>
                {healthy
                  ? 'The recruitment platform is running normally.'
                  : 'One or more checks below need attention.'}
              </div>
            </div>
          </div>

          {/* Component cards */}
          <div className="grid gap-3 sm:grid-cols-2">
            {data.components.map((c) => {
              const look = LOOK[c.status] || LOOK.info;
              const CompIcon = COMPONENT_ICON[c.key] || Info;
              return (
                <div key={c.key} className={`rounded-xl border px-4 py-4 ${look.box}`}>
                  <div className="flex items-start gap-3">
                    <CompIcon className={`${look.iconColor} shrink-0 mt-0.5`} size={20} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className={`font-semibold ${look.text}`}>{c.label}</div>
                        <look.Icon className={`${look.iconColor} shrink-0`} size={16} />
                      </div>
                      <div className="mt-0.5 text-sm text-slate-600 break-words">{c.detail}</div>
                      {c.key === 'leads' && c.latestAt && (
                        <div className="mt-1 text-xs text-slate-500">Latest application {fmt(c.latestAt)}</div>
                      )}
                      {c.key === 'messaging' && c.checkedAt && (
                        <div className="mt-1 text-xs text-slate-500">
                          Checked {fmt(c.checkedAt)}{c.stale ? ' · this reading is stale' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-between text-xs text-slate-400">
            <span>Read-only — this screen changes nothing.</span>
            {lastLoaded && <span>Updated {fmt(lastLoaded)} · refreshes every 30s</span>}
          </div>
        </>
      )}
    </div>
  );
}
